import { designVariables as tk } from "@studio3t/design-variables";
import { tabsTokens } from "@studio3t/generic-components";
import { createGlobalStyle } from "styled-components";

/**
 * OverlayScrollbars theming, mirrored from mario's GlobalStyles
 * (ui/src/theming/GlobalStyles.ts) so scrollbars rendered through
 * StyledTableScroll look the same as the common-ui tables in mario:
 * thin token-sized thumb that widens on hover, themed colors, padded track.
 */
export const GlobalStyles = createGlobalStyle`
  .os-scrollbar {
    box-sizing: border-box;

    --os-handle-perpendicular-size: ${tk.ui.scrollbar.thumbwidths}px;
    --os-handle-perpendicular-size-hover: ${tk.ui.scrollbar.thumbwidthl}px;
    --os-handle-perpendicular-size-active: ${tk.ui.scrollbar.thumbwidthl}px;

    --os-handle-bg: ${({ theme }) => theme.scrollbarThumbColor};
    --os-handle-bg-hover: ${({ theme }) => theme.scrollbarThumbHoveredColor};
    --os-handle-bg-active: ${({ theme }) => theme.scrollbarThumbActiveColor};

    --os-handle-border-radius: ${tk.ui.scrollbar.corner}px;

    --os-track-bg-hover: ${({ theme }) => theme.scrollbarTrackHoveredColor};
    --os-track-bg-active: ${({ theme }) => theme.scrollbarTrackHoveredColor};

    --os-track-border-radius: ${tk.ui.scrollbar.trackcorner}px;
    --os-handle-interactive-area-offset: 0;
  }

  .os-scrollbar-vertical .os-scrollbar-track {
    box-sizing: border-box;
    width: ${tk.ui.scrollbar.trackwidth}px;
    padding: ${tk.ui.scrollbar.padding}px 0 !important;
    right: 4px;
  }

  .os-scrollbar-horizontal .os-scrollbar-track {
    box-sizing: border-box;
    height: ${tk.ui.scrollbar.trackwidth}px;
    padding: 0 ${tk.ui.scrollbar.padding}px !important;
    bottom: 4px;
  }

  .os-scrollbar-vertical .os-scrollbar-handle {
    margin-right: 1px;
    margin-left: 1px;
  }

  /* The handle is absolutely positioned at the track's bottom edge, so a handle
     thinner than the track (6/8px thumb in a 10px track) bottom-aligns instead of
     centering on the grey track area. Push it up by half the slack to center it —
     per state, since the thumb widens on hover/active. */
  .os-scrollbar-horizontal .os-scrollbar-handle {
    margin-bottom: ${(tk.ui.scrollbar.trackwidth - tk.ui.scrollbar.thumbwidths) / 2}px;
  }

  .os-scrollbar-horizontal:hover .os-scrollbar-handle,
  .os-scrollbar-horizontal:active .os-scrollbar-handle {
    margin-bottom: ${(tk.ui.scrollbar.trackwidth - tk.ui.scrollbar.thumbwidthl) / 2}px;
  }

  .os-scrollbar-horizontal {
    margin-right: 15px;
  }

  /* Scrollbars show only while the pointer is over their scroll region
     (TTTL-261). The library's auto-hide (set app-wide in main.tsx) also
     reveals a bar on every scroll event, programmatic ones included — the
     chat pinning to its newest message, the log tail, a panel resetting to
     the top — so a bar flashed on page load with no pointer near it. On
     pointer devices gate on the host's :hover instead; the library keeps
     driving the fade and the touch path. A thumb drag holds the pointer
     captured, so the host stays :hover even when the pointer leaves it.
     The delay matches autoHideDelay in main.tsx. */
  @media (hover: hover) {
    [data-overlayscrollbars]:not(:hover) > .os-scrollbar-auto-hide {
      opacity: 0;
      visibility: hidden;
      transition-delay: 0.5s;
    }
  }

  /* Keyboard focus (TTTL-186). The library's global reset unsets every
     button and input (all: unset) and its buttons draw a transparent
     outline with no :focus-visible rule, so keyboard focus was invisible
     on most controls. One ring for every focusable element, in the
     design's focus colour, only when focus comes from the keyboard —
     pointer clicks show nothing, as before. !important so a component's
     own "outline: none" on :focus cannot hide it; the two custom
     properties are the sanctioned way to vary it per control. Controls
     draw the ring inside their own box: a ring outside the box is cut
     off wherever a scroll container or a clipped card ends (table row
     actions, the AI Helper's code blocks, the navigation rail), and
     inside it is whole on all four sides. A tabindex of -1 marks a
     script-only focus target (a modal card, a panel) — not a control, so
     no ring. */
  :is(a, button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]):focus-visible {
    outline: var(--focus-ring, ${tk.ui.semantic.focusstroke}px solid ${tk.theme.light.semantic.focusprimary}) !important;
    outline-offset: var(--focus-ring-offset, -${tk.ui.semantic.focusstroke}px) !important;
  }
  /* Text entry matches :focus-visible on a mouse click as well (the
     browser assumes typing needs the caret), which would ring every
     clicked field. Its ring waits for keyboard navigation instead — the
     root's data-focus-modality, kept by installFocusModality. */
  :root:not([data-focus-modality="keyboard"]) :is(input:not([type="checkbox"]):not([type="radio"]), textarea, [contenteditable="true"]):focus-visible {
    outline: none !important;
  }
  /* The browser keeps keyboard mode across a pointer click that never
     moves focus itself: the library's dropdown trigger cancels its
     mousedown, focuses the menu by script, and after a pointer pick hands
     focus back to the trigger by script — so once anything was typed on
     the page, choosing Ask or Act with the mouse left the trigger ringed.
     The root's modality knows the last press was a pointer, and the ring
     waits for the next navigation key (Tab, Enter, Space, arrows). */
  :root[data-focus-modality="pointer"] :is(a, button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]):focus-visible {
    outline: none !important;
  }
  /* A checked checkbox or radio is filled with the same green, so an
     inset ring would vanish on it — theirs sits outside, past a gap of
     background, and stays distinct in either state. */
  input[type="checkbox"],
  input[type="radio"] {
    --focus-ring-offset: ${tk.ui.semantic.focusstroke}px;
  }
  /* No ring inside an open menu or listbox (TTTL-260). The library
     highlights the active row itself, so a ring on top of it says
     nothing new — and the library also moves focus to whichever item
     the pointer passes over, which would drag the ring along with the
     mouse once the browser is in keyboard mode (any key pressed since
     the last click). The trigger outside the menu keeps its ring. The
     property inherits, so the container clears it for every item. */
  [role="menu"],
  [role="listbox"] {
    --focus-ring: none;
  }
  /* The library's tabs draw their own ring just outside the tab's box,
     so its bottom edge sits below the hairline as a line of its own
     rather than merging with the active tab's underline. The app's ring
     takes the same shape there; the library's tokens keep the two in
     step. */
  [role="tab"] {
    --focus-ring: ${tabsTokens.focusRing}px solid ${tk.theme.light.semantic.focusprimary};
    --focus-ring-offset: ${tabsTokens.focusOffset}px;
  }
  /* The library switch hides its input (0×0, transparent) behind a label
     that draws the track — the ring goes on the track, outside it. */
  label > input[type="checkbox"] {
    --focus-ring: none;
  }
  label:has(> input[type="checkbox"]:focus-visible) {
    outline: ${tk.ui.semantic.focusstroke}px solid ${tk.theme.light.semantic.focusprimary};
    outline-offset: ${tk.ui.semantic.focusstroke}px;
  }
  /* OverlayScrollbars makes each scroll viewport a tab stop for keyboard
     scrolling and draws no ring on it (its own stylesheet); a ring around
     a whole page region is not what that focus means, so it stays off. */
  [data-overlayscrollbars-viewport] {
    --focus-ring: none;
  }
  /* Custom properties inherit — the viewport's children take the ring
     back for everything inside the scroll region. */
  [data-overlayscrollbars-viewport] > * {
    --focus-ring: initial;
  }
  /* …except inside a menu or listbox that scrolls: its items sit in a
     viewport too, and they stay without a ring (the rule above for menus). */
  :is([role="menu"], [role="listbox"]) [data-overlayscrollbars-viewport] > * {
    --focus-ring: none;
  }
  /* The library radio's input is unset to nothing (0×0, transparent)
     behind a label that draws the circle as the input's next sibling, so
     a ring on the input is as invisible as the input. The ring rides the
     circle, round like it, past a gap of background. */
  label > input[type="radio"] {
    --focus-ring: none;
  }
  label > input[type="radio"]:focus-visible + span {
    outline: ${tk.ui.semantic.focusstroke}px solid ${tk.theme.light.semantic.focusprimary};
    outline-offset: ${tk.ui.semantic.focusstroke}px;
  }
  /* The library checkbox's input shifts a pixel between its states (the
     wrapper's border comes and goes under it), which would make the ring
     jump on toggle — so the ring rides the wrapper, whose box is fixed. */
  .generic-checkbox-wrapper > input[type="checkbox"] {
    --focus-ring: none;
  }
  .generic-checkbox-wrapper:has(> input[type="checkbox"]:focus-visible) {
    outline: ${tk.ui.semantic.focusstroke}px solid ${tk.theme.light.semantic.focusprimary};
    outline-offset: ${tk.ui.semantic.focusstroke}px;
  }
`;
